import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Profile } from "@/integrations/supabase/types/subscription";
import { Globe } from "lucide-react";
import { useLanguage } from "@/components/language/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";

interface LanguageCardProps {
  profile: Profile;
  onUpdate: (updates: Partial<Profile>) => Promise<void>;
}

export function LanguageCard({ profile, onUpdate }: LanguageCardProps) {
  const { language, setLanguage, t } = useLanguage();
  const { toast } = useToast();

  // Effect to sync profile language with context
  useEffect(() => {
    if (profile.language && profile.language !== language) {
      setLanguage(profile.language as 'no' | 'en');
    }
  }, [profile.language, language, setLanguage]);

  const handleLanguageChange = async (value: string) => {
    try {
      await setLanguage(value as 'no' | 'en');
      await onUpdate({ language: value });
      
      const successMessage = value === 'no' 
        ? 'Språk er oppdatert til Norsk' 
        : 'Language has been updated to English';
      
      toast({
        title: t('settings.language.success'),
        description: successMessage,
        duration: 3000,
      });

      // Force reload to apply language changes throughout the app
      window.location.reload();
    } catch (error) {
      console.error('Error updating language:', error);
      toast({
        variant: "destructive",
        title: t('settings.language.error'),
        description: error instanceof Error ? error.message : 'An error occurred',
      });
    }
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg bg-white/50 backdrop-blur-sm">
      <CardHeader className="space-y-1">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100">
            <Globe className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-xl text-orange-900">
              {t('settings.language.title')}
            </CardTitle>
            <CardDescription className="text-sm text-orange-600/80">
              {t('settings.language.description')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Select
          value={language}
          onValueChange={handleLanguageChange}
        >
          <SelectTrigger className="w-full sm:w-[200px] border-orange-200 focus:ring-orange-500">
            <SelectValue placeholder={t('settings.language.select_placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="no">{t('settings.language.norwegian')}</SelectItem>
            <SelectItem value="en">{t('settings.language.english')}</SelectItem>
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
}